import { Search as SearchIcon } from "@faire/slate/icons/Search";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { InputBase } from "@faire/slate/private/form/InputBase";
import { ClearButton } from "@faire/slate/private/search/ClearButton";
import type {
  ISearchWebProps,
  SearchVariant,
} from "@faire/slate/private/search/types";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

type IProps = {
  /**
   * Whether the text input component is disabled.
   */
  disabled?: boolean;

  /**
   * The function to call when the clear button is clicked.
   * If defined, the clear button will be shown when the input has a value.
   */
  onClear?: () => void;

  /**
   * The placeholder of the text input component.
   */
  placeholder?: string;

  /**
   * The value of the text input component.
   */
  value?: string;

  /**
   * The variant ({@link SearchVariant}) of the search input component.
   *
   * @default "page"
   */
  variant?: SearchVariant;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ISearchWebProps;

/**
 * Search Inputs allow users to quickly and easily find what they are looking for.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.1.0
 */
export const Search: React.FC<IProps> = ({
  disabled,
  onClear,
  onChange,
  ref,
  variant = "page",
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasValue, setHasValue] = useState(!!props.value);
  const showClearButton = onClear && hasValue && !disabled;
  const searchCls = searchStyles.cls({ variant });

  const onContainerClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(!!event.target.value);
      onChange?.(event);
    },
    [onChange]
  );

  const onClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClear?.();
      inputRef.current?.focus();
    },
    [onClear]
  );

  useImperativeHandle(ref, () => inputRef.current!);

  useEffect(() => {
    setHasValue(!!props.value);
  }, [props.value]);

  return (
    <InputBase
      className={componentCls(searchCls)}
      disabled={disabled}
      onChange={onInputChange}
      onContainerClick={onContainerClick}
      prefix={<SearchIcon className="text-base" titleAccess="" />}
      ref={inputRef}
      suffix={showClearButton ? <ClearButton onClick={onClearClick} /> : null}
      type="search"
      {...props}
    />
  );
};

const searchStyles = tsv({
  base: "fs-search",
  variants: {
    variant: {
      page: "fs-search--page",
      global: "fs-search--global",
    },
  },
});
